import {DNA_COMPLEMENT_GAP_TABLE, NucleotideType, RNA_COMPLEMENT_GAP_TABLE} from 'biojs/BioConstants';
import revcomp from 'biojs/revcomp';
import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

// RNA is detected only when the sequence contains U (upper or lower) but no T at all. Any
// other input — pure DNA, mixed T+U, all-ambiguity-code, or empty — resolves to DNA. This
// mirrors the spec's "T beats U" rule: a U in an otherwise-DNA string is left
// uncomplemented by the DNA table's pass-through behavior rather than being an error.
function detectNucleotideType(seq: string): NucleotideType {
  const hasU = /[Uu]/.test(seq);
  const hasT = /[Tt]/.test(seq);
  return hasU && !hasT ? NucleotideType.RNA : NucleotideType.DNA;
}

export default {
  name: 'REVERSECOMPLEMENT',
  shortDescription: 'Reverses and complements a nucleotide sequence.',
  category: FunctionCategory.TEXT,
  description:
    'Returns the reverse complement of a DNA or RNA sequence: the sequence is reversed and each ' +
    'base is replaced with its complement (A/T for DNA, A/U for RNA, G/C, plus IUPAC ambiguity ' +
    'codes). RNA is detected when the sequence contains U but no T; otherwise DNA rules are used. ' +
    'Gaps ("-") are preserved. Characters that are not recognized nucleotide codes are kept as-is ' +
    'at their mirrored position. Case is preserved per character.',
  params: [
    {
      name: 'sequence',
      type: ArgType.singularValue,
      // coerce a number-like cell to its formatted string representation (e.g. 123 -> "123") instead of a JS
      // number, so eval always receives a string.
      coerce: (value: TypedValue, context?) =>
        formatTypedValue(value, undefined, undefined, undefined, context),
      description: 'The nucleotide sequence to reverse-complement.',
    },
  ],
  returnType: ExcessUtil.VALUE_TYPES.STRING,
  eval: (sequence: string): string => {
    const table =
      detectNucleotideType(sequence) === NucleotideType.RNA
        ? RNA_COMPLEMENT_GAP_TABLE
        : DNA_COMPLEMENT_GAP_TABLE;
    return revcomp(sequence, table);
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;
